import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { Button, Dialog, Field, Input, useToast } from './ui';

/**
 * Anmeldeadresse eines Zugangs ändern.
 *
 * Genutzt in der Zugangskarte an der Person. Die Folgen stehen im
 * Dialog, bevor gespeichert wird: Das Passwort bleibt, offene Einladungen und
 * Rücksetz-Links verfallen, die bisherige Adresse wird benachrichtigt.
 */
export function ChangeLoginEmailDialog({
  open,
  onClose,
  userId,
  personName,
  currentEmail,
}: {
  open: boolean;
  onClose: () => void;
  userId: string | null;
  personName: string;
  currentEmail: string | null;
}): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setEmail('');
      setError(undefined);
    }
  }, [open]);

  const trimmed = email.trim();
  const valid = /^\S+@\S+\.\S+$/.test(trimmed) && trimmed.toLowerCase() !== currentEmail?.toLowerCase();

  const save = useMutation({
    mutationFn: () =>
      api.patch<{ email: string; notified: boolean }>(`/api/operations/accounts/${userId}/email`, { email: trimmed }),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['operation-accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['persons'] }),
        queryClient.invalidateQueries({ queryKey: ['person'] }),
        queryClient.invalidateQueries({ queryKey: ['auth', 'me'] }),
      ]);
      toast.show(result.notified
        ? `Adresse geändert auf ${result.email} – die bisherige Adresse wurde benachrichtigt`
        : `Adresse geändert auf ${result.email}`);
      onClose();
    },
    onError: (err: unknown) => {
      const message = err instanceof ApiError ? err.message : 'Adresse konnte nicht geändert werden';
      setError(message);
      toast.show(message, 'error');
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Anmeldeadresse von ${personName}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button loading={save.isPending} disabled={!valid} onClick={() => save.mutate()}>
            Adresse ändern
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) save.mutate();
        }}
      >
        <p className="text-sm text-slate-600">
          Bisher: <span className="font-medium text-slate-800">{currentEmail}</span>
        </p>
        <Field label="Neue Adresse" required error={error}>
          {(id) => (
            <Input
              id={id}
              type="email"
              autoComplete="off"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setError(undefined);
              }}
              placeholder="name@verein.at"
            />
          )}
        </Field>
        <ul className="list-disc space-y-1 pl-5 text-xs text-slate-500">
          <li>Das Passwort bleibt gleich, bestehende Anmeldungen bleiben aktiv.</li>
          <li>Offene Einladungen und Links zum Zurücksetzen des Passworts verfallen.</li>
          <li>Die bisherige Adresse erhält eine Nachricht über die Änderung.</li>
        </ul>
      </form>
    </Dialog>
  );
}
